import { useCallback, useEffect } from 'react';
import { generatePath } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';

import { ActivitiesContentStatus } from 'global/constants';
import { clearActiveSectionsFromCache } from 'approot/activity-builder/activity-builder.apollo.utils';
import { ExtraSectionIdentifiers } from 'approot/activity-builder/activity-builder.apollo';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { getEditUrl } from 'approot/activity-builder/activity-builder-url.utils';
import { getUserClassPath } from 'approot/classes/classes.apollo';
import { Box, BoxList } from 'approot/shared/design-system/box/box.style';
import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import InlineSvg from 'approot/shared/inline-svg/inline-svg';
import {
  SettingsDrawerButton,
  SettingsDrawerLink,
} from 'approot/activity-builder/settings/settings-drawer.style';
import { ACTIVITY_BUILDER_SETTINGS } from '../activity-builder-present-skip-links';
import {
  PresentMenuContainer,
  PresentMenuStudentStatus,
} from './present-menu.style';
import { PresentDrawerItem } from '../activity-builder-present.constants';
import { useActivityBuilderPresentContext } from '../activity-builder-present.context';
import { useCanAccessEdit } from 'approot/activity-builder/permissions/activities-permissions.hooks';
import { pushActivityBuilderPresentMenuEvent } from '../activity-builder-present-data-tracking.utils';
import {
  EVENT_ACTION_EXIT,
  EVENT_ACTION_OPEN,
  EVENT_TARGET_TYPE_ACTIVITIES,
  EVENT_TARGET_TYPE_MENU,
} from 'approot/shared/data-tracker/data-events.constants';
import { SectionOrSubSectionOverview } from 'approot/activity-builder/activity-builder.constants';
import IconActivitiesActive from 'approot/shared/icons/icon-activities-active';
import IconActivities from 'approot/shared/icons/icon-activities';
import IconFeedback from 'approot/shared/icons/icon-feedback';
import IconDownload from 'approot/shared/icons/icon-download';
import IconSettings from 'approot/shared/icons/icon-settings';
import IconLeave from 'approot/shared/icons/icon-leave';
import IconTranslateActive from 'approot/shared/icons/icon-translate-active';
import IconTranslate from 'approot/shared/icons/icon-translate';
import IconNavSchoolBag from 'approot/shared/inline-svg/navigation/icon-nav-school-bag';
import IconNavSchoolBagActive from 'approot/shared/inline-svg/navigation/icon-nav-school-bag-active';
import PresentMenuDownloadOptions from './present-menu-download-options';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import FeedbackSurveyModal from 'approot/shared/feedback-survey/feedback-survey-modal';
import { FEEDBACK_DIGITAL_SLIDES } from 'global/constants';
import { useLiveClassroomStudents } from 'approot/activity-builder/classroom/subscription/live-classroom-students.apollo';
import Text from 'approot/shared/styles/typography/text';
import { useWsClientStatus } from 'lib/ws/ws-apollo';

type Props = {
  isPublic: boolean;
  contentSlug: string;
  activeSectionId: string;
  sectionIds: string[];
  status: ActivitiesContentStatus;
  handleDownload: () => void;
  handleDownloadStudentAnswers: () => void;
  currentSectionOverview: SectionOrSubSectionOverview | null;
  currentSectionOverviewIndex: number;
  sectionsOverview: SectionOrSubSectionOverview[];
};

const PresentMenuOptions = ({
  isPublic,
  contentSlug,
  activeSectionId,
  sectionIds,
  status,
  handleDownload,
  handleDownloadStudentAnswers,
  currentSectionOverview,
  sectionsOverview,
  currentSectionOverviewIndex,
}: Props) => {
  const client = useApolloClient();
  const { openModal, isVisible, closeModal } = useSimpleModalState();

  const canEditActivities = useCanAccessEdit(status) && !isPublic;

  const { onlineStudents, students } = useLiveClassroomStudents();
  const { isConnectionActive } = useWsClientStatus();

  const hasStudents = students.length > 0;

  const { drawer, drawerType, setDrawer, isZoomToFit } =
    useActivityBuilderPresentContext();
  const { slug, userClassId, contentType, contentId, version } =
    useGetInteractiveLessonParams();

  const handleShowSurvey = () => {
    openModal();
  };

  const handleOpen = useCallback(
    (type: PresentDrawerItem) => {
      if (drawerType === type) {
        setDrawer({
          isActive: true,
          type: PresentDrawerItem.None,
        });
        return;
      }
      setDrawer({
        isActive: true,
        type,
      });
    },
    [drawerType, setDrawer]
  );

  // when menu appears focus the container
  useEffect(() => {
    if (drawer.isActive) {
      document.getElementById(ACTIVITY_BUILDER_SETTINGS)?.focus();
    }
  }, [drawer.isActive]);

  return (
    <>
      <FeedbackSurveyModal
        isVisible={isVisible}
        closeModal={closeModal}
        feature={FEEDBACK_DIGITAL_SLIDES}
      />
      <PresentMenuContainer
        id={ACTIVITY_BUILDER_SETTINGS}
        aria-label="Presentation Menu Options"
        tabIndex={-1}
        data-selector="present-menu-options"
      >
        <BoxList role="group" aria-label="Main Menu Actions">
          <li>
            <SettingsDrawerButton
              data-selector="present-menu-activities"
              type="button"
              $isActive={drawerType === PresentDrawerItem.Activities}
              data-testid="activities"
              onClick={() => {
                pushActivityBuilderPresentMenuEvent({
                  eventAction: EVENT_ACTION_OPEN,
                  eventTargetIdentifier: EVENT_TARGET_TYPE_ACTIVITIES,
                  contentId,
                  contentType,
                  sectionId: activeSectionId,
                  currentSectionOverview,
                  sectionsOverview,
                  version,
                  status,
                  isZoomToFit,
                  currentSectionOverviewIndex,
                });

                handleOpen(PresentDrawerItem.Activities);
              }}
            >
              {drawerType === PresentDrawerItem.Activities ? (
                <IconActivitiesActive width={24} height={24} rotate={270} />
              ) : (
                <IconActivities width={24} height={24} rotate={270} />
              )}
              <span>Activities</span>
            </SettingsDrawerButton>
          </li>
          <li>
            <SettingsDrawerButton
              data-selector="present-menu-tls"
              type="button"
              data-testid="present-menu-teaching-notes"
              $isActive={drawerType === PresentDrawerItem.TeachingNotes}
              onClick={() => handleOpen(PresentDrawerItem.TeachingNotes)}
            >
              {drawerType === PresentDrawerItem.TeachingNotes ? (
                <ActivityBuilderInlineSvg
                  id="icon_book_active"
                  width={20}
                  height={20}
                />
              ) : (
                <InlineSvg id="book" width={20} height={20} />
              )}
              <span>Teaching Sequence</span>
            </SettingsDrawerButton>
          </li>
          {!isPublic && (
            <li>
              <SettingsDrawerButton
                data-selector="present-menu-students"
                type="button"
                data-testid="present-menu-students"
                $isActive={drawerType === PresentDrawerItem.Students}
                onClick={() => handleOpen(PresentDrawerItem.Students)}
              >
                {drawerType === PresentDrawerItem.Students ? (
                  <IconNavSchoolBagActive width={20} height={20} />
                ) : (
                  <IconNavSchoolBag width={20} height={20} />
                )}
                <div>
                  <span>Students</span>
                  {hasStudents && (
                    <Box $gap={4} $alignItems="center" $marginTop={8}>
                      <PresentMenuStudentStatus
                        $isOnline={onlineStudents.length > 0}
                        $isNeutral={!isConnectionActive}
                      />
                      <Text
                        $colour="grey"
                        $fontWeight="normal"
                        $fontSize={10}
                        $align="center"
                      >
                        {!isConnectionActive
                          ? `? / ${students.length}`
                          : `${onlineStudents.length} / ${students.length}`}
                      </Text>
                    </Box>
                  )}
                </div>
              </SettingsDrawerButton>
            </li>
          )}
          {version && (
            <li>
              <SettingsDrawerButton
                data-selector="present-menu-translate"
                type="button"
                data-testid="present-menu-translate"
                $isActive={drawerType === PresentDrawerItem.Translate}
                onClick={() => handleOpen(PresentDrawerItem.Translate)}
              >
                {drawerType === PresentDrawerItem.Translate ? (
                  <IconTranslateActive width={20} height={20} />
                ) : (
                  <IconTranslate width={20} height={20} />
                )}
                <span>Translate</span>
              </SettingsDrawerButton>
            </li>
          )}
        </BoxList>
        <BoxList role="group" aria-label="Secondary Menu Actions">
          {canEditActivities && (
            <li>
              <SettingsDrawerLink
                to={getEditUrl({
                  slug,
                  userClassId,
                  sectionId: version
                    ? ExtraSectionIdentifiers.Introduction
                    : activeSectionId,
                  contentType,
                })}
                $isActive={false}
                onClick={() => {
                  if (version) {
                    // clear sections to handle differences in published versions
                    clearActiveSectionsFromCache({ client, sectionIds });
                  }
                }}
                data-selector="present-menu-edit"
              >
                <ActivityBuilderInlineSvg
                  id="icon_pencil"
                  width={24}
                  height={24}
                />
                <span>Edit</span>
              </SettingsDrawerLink>
            </li>
          )}
          {!canEditActivities && !isPublic && (
            <li>
              <SettingsDrawerButton
                type="button"
                $isActive={false}
                aria-haspopup="dialog"
                onClick={handleShowSurvey}
              >
                <IconFeedback width={24} height={24} />
                <span>Give us Feedback</span>
              </SettingsDrawerButton>
            </li>
          )}
          {version && !isPublic && (
            <li>
              <SettingsDrawerButton
                type="button"
                $isActive={drawerType === PresentDrawerItem.Settings}
                aria-haspopup="dialog"
                data-selector="lb-drawer-lesson-settings-btn"
                onClick={() => {
                  setDrawer({
                    isActive: true,
                    type: PresentDrawerItem.Settings,
                  });
                }}
              >
                {drawerType === PresentDrawerItem.Settings ? (
                  <ActivityBuilderInlineSvg
                    id="icon_settings_active"
                    width={20}
                    height={20}
                  />
                ) : (
                  <IconSettings width={20} height={20} />
                )}
                <span>Settings</span>
              </SettingsDrawerButton>
            </li>
          )}

          <li>
            {!version || isPublic ? (
              <SettingsDrawerButton
                data-selector="present-menu-download"
                type="button"
                aria-haspopup="dialog"
                $isActive={false}
                onClick={handleDownload}
              >
                <IconDownload width={24} height={24} />
                <span>Download</span>
              </SettingsDrawerButton>
            ) : (
              <PresentMenuDownloadOptions
                onClickDownloadActivities={handleDownload}
                onClickDownloadResponses={handleDownloadStudentAnswers}
              />
            )}
          </li>
          <li>
            <SettingsDrawerLink
              data-selector="present-menu-exit"
              to={getUserClassPath(generatePath(contentSlug))}
              $isActive={false}
              data-testid="exit"
              onClick={() => {
                pushActivityBuilderPresentMenuEvent({
                  eventAction: EVENT_ACTION_EXIT,
                  eventTargetIdentifier: EVENT_TARGET_TYPE_MENU,
                  contentId,
                  contentType,
                  sectionId: activeSectionId,
                  currentSectionOverview,
                  sectionsOverview,
                  version,
                  status,
                  isZoomToFit,
                  currentSectionOverviewIndex,
                });

                if (!version) {
                  clearActiveSectionsFromCache({ client, sectionIds });
                }
              }}
            >
              <IconLeave width={24} height={24} />
              <span>Exit</span>
            </SettingsDrawerLink>
          </li>
        </BoxList>
      </PresentMenuContainer>
    </>
  );
};

export default PresentMenuOptions;
